import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, CircularProgress, Divider } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { READ_IMPORT_PLANNING_BUDGET, READ_IMPORT_SHIPPING_BOOKING } from '../../../services/apiRoutes';
import CustomCardView from "../../common/CustomCardView";
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import { useCompanyDateFormat } from "../../../constants/useCompanyDateFormat";
import { t } from "../../../constants/translationHelper";


const PlanningBudgetExportView = ({ handleClose, setMessage, fetchData, planningBudgetId, shippingLines, fclData, directListData, freightForwardAgencyData, customClearingAgencyData, cifFobList, currencyList, clientCompanyId }) => {
    const companyDateFormat = useCompanyDateFormat();

    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!planningBudgetId) return;

        const fetchData = async () => {
            try {
                const accessToken = sessionStorage.getItem('accessToken');
                const response = await axios.get(READ_IMPORT_PLANNING_BUDGET(planningBudgetId), {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
                setData(response.data.planningBudget);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [planningBudgetId]);

    if (loading) return <CircularProgress />;
    if (error) return <p>Error: {error}</p>;
    const labels = [
        { key: 'shipmentId', label: "Shipment ID", sm: 12, md: 6, },
        { key: 'baseCurrency', label: "Base Currency", sm: 12, md: 6, },
        { key: 'currency', label: "Currency", sm: 12, md: 6, },
        { key: 'proformaInvoiceNo', label: "Proforma Invoice", sm: 12, md: 6, },
        { key: 'commercialInvoiceNo', label: "Commercial Invoice", sm: 12, md: 6, },
        { key: 'seaFreight', label: "Sea Freight", sm: 12, md: 6, },
        { key: 'clearingAgencyCharge', label: "Clearing Agency Charge", sm: 12, md: 6, },
        { key: 'freightForwardAgencyCharge', label: "Freight Forward Agency Charge", sm: 12, md: 6, },
        { key: 'cfsLocalCharges', label: "CFS Local Charges", sm: 12, md: 6, },
        { key: 'shippingLineCharges', label: "Shipping Line Charges", sm: 12, md: 6, },
        { key: 'blCharges', label: "BL Charges", sm: 12, md: 6, },
        { key: 'othersCharges', label: "Others Charges", sm: 12, md: 6, },
        { key: 'dutyCharge', label: "Duty Charge", sm: 12, md: 6, },
        { key: 'taxCharge', label: "Tax Charge", sm: 12, md: 6, },
        { key: 'transportCharge', label: "Transport Charge", sm: 12, md: 6, },
        { key: 'conversionRate', label: "Conversion Rate", sm: 12, md: 6, },
        { key: 'createdAt', label: "Created Date", sm: 12, md: 6, },
        { key: 'updatedAt', label: "Updated Date", sm: 12, md: 6, },
    ];

    const values = {
        shipmentId: data?.shipmentId,
        baseCurrency: `${data?.baseCurrency?.currency} - ${data?.baseCurrency?.currency_name}`,
        currency: `${data?.currency?.currency} - ${data?.currency?.currency_name}`,
        proformaInvoiceNo: data?.ProformaInvExp?.proformaInvoiceNo || '-',
        commercialInvoiceNo: data?.CommercialInvExp?.commercialInvoiceNo || '-',
        seaFreight: `${data?.currency?.currency_symbol || ''} ${parseFloat(data?.seaFreight || 0).toFixed(2)}`,
        clearingAgencyCharge: `${data?.baseCurrency?.currency_symbol || ''} ${parseFloat(data?.clearingAgencyCharge || 0).toFixed(2)}`,
        freightForwardAgencyCharge: `${data?.baseCurrency?.currency_symbol || ''} ${parseFloat(data?.freightForwardAgencyCharge || 0).toFixed(2)}`,
        cfsLocalCharges: `${data?.baseCurrency?.currency_symbol || ''} ${parseFloat(data?.cfsLocalCharges || 0).toFixed(2)}`,
        shippingLineCharges: `${data?.baseCurrency?.currency_symbol || ''} ${parseFloat(data?.shippingLineCharges || 0).toFixed(2)}`,
        blCharges: `${data?.baseCurrency?.currency_symbol || ''} ${parseFloat(data?.blCharges || 0).toFixed(2)}`,
        othersCharges: `${data?.baseCurrency?.currency_symbol || ''} ${parseFloat(data?.othersCharges || 0).toFixed(2)}`,
        dutyCharge: `${data?.baseCurrency?.currency_symbol || ''} ${parseFloat(data?.dutyCharge || 0).toFixed(2)}`,
        taxCharge: `${data?.baseCurrency?.currency_symbol || ''} ${parseFloat(data?.taxCharge || 0).toFixed(2)}`,
        transportCharge: `${data?.baseCurrency?.currency_symbol || ''} ${parseFloat(data?.transportCharge || 0).toFixed(2)}`,
        conversionRate: `${data?.baseCurrency?.currency_symbol || ''} ${parseFloat(data?.conversionRate || 0).toFixed(2)}`,
        createdAt: companyDateFormat(data?.createdAt) || '',
        updatedAt: companyDateFormat(data?.updatedAt) || '',
    };

    return (
        <Box sx={{ mx: 'auto', mt: 0 }}>
            <Formik initialValues={values} enableReinitialize>
                {() => (
                    <Form>
                        <Grid container spacing={1}>
                            <Grid item xs={4}>
                                <Typography sx={{ fontWeight: 600, fontSize: "18px", color: "#212529" }}>
                                    {t("Planning & Budget")}
                                </Typography>
                            </Grid>
                            <Grid item xs={8} style={{ display: "flex", justifyContent: "flex-end" }}>
                                <Box>
                                    <IconButton aria-label="delete">
                                        <DeleteOutlineOutlinedIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                    </IconButton>
                                    <IconButton aria-label="print">
                                        <LocalPrintshopOutlinedIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                    </IconButton>
                                    <IconButton aria-label="close" onClick={handleClose} >
                                        <CloseIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                    </IconButton>
                                </Box>
                            </Grid>
                        </Grid>
                        <Divider />
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <CustomCardView label={labels} data={values} />
                            </Grid>

                        </Grid>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};

export default PlanningBudgetExportView;


